import axios from 'axios';
import * as types from '../types';
import { IAttachmentsApi } from '../contracts';
import { HttpApi } from './HttpApi';

const PARENT_PATHS: Record<types.AttachmentParentType, string> = {
  CUSTOMER: 'customers',
  PROPERTY: 'properties',
  JOB: 'jobs',
};

function parentPath(parentType: types.AttachmentParentType, parentId: string): string {
  const segment = PARENT_PATHS[parentType];
  if (!segment) throw new Error(`Unknown attachment parent type: ${parentType}`);
  return `/${segment}/${encodeURIComponent(parentId)}/attachments`;
}

/** Read a Blob as text (FileReader: works in every browser and in jsdom). */
function blobText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * With responseType 'blob' an error body arrives as a Blob too. Parse it
 * back into the usual `{ success, message, code }` object so getErrorCode /
 * getErrorMessage work as for any other request.
 */
export async function withJsonErrorBody(error: unknown): Promise<unknown> {
  if (axios.isAxiosError(error) && error.response && typeof Blob !== 'undefined' && error.response.data instanceof Blob) {
    try {
      const text = await blobText(error.response.data);
      error.response.data = text ? JSON.parse(text) : null;
    } catch {
      error.response.data = null;
    }
  }
  return error;
}

export class AttachmentsHttpApi extends HttpApi implements IAttachmentsApi {
  async listAttachments(
    parentType: types.AttachmentParentType,
    parentId: string,
    page = 1,
    limit = 25
  ): Promise<types.PaginatedResponse<types.Attachment>> {
    const response = await this.http.get<types.PaginatedResponse<types.Attachment>>(parentPath(parentType, parentId), {
      params: { page, limit },
    });
    return response.data;
  }

  async uploadAttachment(
    parentType: types.AttachmentParentType,
    parentId: string,
    file: File,
    options: types.UploadAttachmentOptions = {}
  ): Promise<types.Attachment> {
    const form = new FormData();
    // Text fields first, so the server has them before the file part
    if (options.description !== undefined && options.description.trim() !== '') {
      form.append('description', options.description);
    }
    form.append('file', file, file.name);
    const { onProgress } = options;
    const response = await this.http.post<types.APIResponse<types.Attachment>>(parentPath(parentType, parentId), form, {
      // The shared instance defaults to application/json, which would make
      // axios serialize the FormData to JSON; the browser adapter replaces
      // this with the real multipart header (boundary included).
      headers: { 'Content-Type': 'multipart/form-data' },
      signal: options.signal,
      onUploadProgress: onProgress
        ? (event) => {
            const total = event.total || file.size;
            if (total > 0) onProgress(Math.min(1, event.loaded / total));
          }
        : undefined,
    });
    return response.data.data;
  }

  async getAttachment(id: string): Promise<types.Attachment> {
    const response = await this.http.get<types.APIResponse<types.Attachment>>(`/attachments/${encodeURIComponent(id)}`);
    return response.data.data;
  }

  async downloadAttachment(id: string, options: types.DownloadAttachmentOptions = {}): Promise<Blob> {
    try {
      const response = await this.http.get<Blob>(`/attachments/${encodeURIComponent(id)}/download`, {
        responseType: 'blob',
        params: options.download ? { download: 'true' } : undefined,
      });
      return response.data;
    } catch (error) {
      throw await withJsonErrorBody(error);
    }
  }

  async updateAttachment(id: string, data: types.UpdateAttachmentRequest): Promise<types.Attachment> {
    const response = await this.http.patch<types.APIResponse<types.Attachment>>(`/attachments/${encodeURIComponent(id)}`, data);
    return response.data.data;
  }

  async deleteAttachment(id: string): Promise<{ id: string }> {
    const response = await this.http.delete<types.APIResponse<{ id: string }>>(`/attachments/${encodeURIComponent(id)}`);
    return response.data.data;
  }
}
